import { datadogLogs } from '@datadog/browser-logs'
import { getCookie } from '@utilities'
import { Amplify } from 'aws-amplify'
import {
  fetchAuthSession,
  signIn,
  signInWithRedirect,
  signOut
} from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import Cookies from 'js-cookie'
import store from 'store'
import { endpoints } from './api'
import history from './history'
import { store as authStore } from './store'

const MINUTES_UNITL_AUTO_LOGOUT = 20160 // 14 days in mins
const CHECK_INTERVAL = 1000 // in ms
const STORE_KEY = 'lastAction'

const REACT_APP_DOMAIN = window.location.hostname.includes('blueprint.ai')
  ? process.env.REACT_APP_DOMAIN
  : process.env.REACT_APP_LEGACY_DOMAIN

class CookieStorage {
  async setItem(key, value) {
    return Cookies.set(key, value, {
      domain: REACT_APP_DOMAIN,
      secure: true,
      sameSite: 'none'
    })
  }
  async getItem(key) {
    return Cookies.get(key)
  }
  async removeItem(key) {
    return Cookies.remove(key, { domain: REACT_APP_DOMAIN })
  }
}

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
      loginWith: {
        email: true,
        phone: false,
        username: false,
        oauth: {
          domain: process.env.REACT_APP_COGNITO_OAUTH_DOMAIN,
          scopes: ['email', 'profile', 'openid'],
          redirectSignIn: [
            `${window.location.origin}/onboarding`,
            `${window.location.origin}/login`
          ],
          redirectSignOut: [window.location.origin],
          responseType: 'code'
        }
      }
    }
  }
})

cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage())

export default class AuthService {
  constructor() {
    this.autoLogoutTimer = null
  }

  getProviderPayload = provider => {
    if (provider === 'google') {
      return {
        provider: 'Google'
      }
    } else if (provider === 'microsoft') {
      return {
        provider: {
          custom: 'Microsoft'
        }
      }
    }
    return null
  }

  authSocial = async provider => {
    const providerPayload = this.getProviderPayload(provider)

    await signInWithRedirect(providerPayload)

    // Ensure we have a valid session after sign-in
    const session = await fetchAuthSession()
    if (!session.tokens) {
      throw new Error('Failed to get session after sign-in')
    }
    this.resetAutoLogoutTime()
    return session
  }

  login = async (email, password) => {
    try {
      await signIn({
        username: email.toLowerCase().trim(),
        password
      })
      // Ensure we have a valid session after sign-in
      const session = await fetchAuthSession()
      if (!session.tokens) {
        throw new Error('Failed to get session after sign-in')
      }
      this.resetAutoLogoutTime()
      return session
    } catch (error) {
      datadogLogs.logger.error('[Auth Debug - Auth] Login Error', { error })
      throw error
    }
  }

  logout = async () => {
    try {
      datadogLogs.logger.info('[Auth Debug - Auth] Logging Out')
      await signOut()
      const isWidget = window.location.pathname.includes('/widget')
      const isMiniWidget = window.location.pathname.includes('/mini-widget')
      const isExtension = window.location.pathname.includes('/extension')
      this.clearAutoLogoutTimer()
      store.remove(STORE_KEY)
      if (isWidget) history.replace('/widget')
      else if (isMiniWidget) history.replace('/mini-widget')
      else if (isExtension) history.replace('/extension')
      else history.replace('/login')
    } catch (error) {
      datadogLogs.logger.error(
        '[Auth Debug - Auth] Error Performing Global Sign Out',
        {
          error,
          errorMessage: error.message || JSON.stringify(error)
        }
      )
      throw error
    }
  }

  widgetLogin = async authTokens => {
    const actions = authStore.getActions()
    const {
      auth: { setSSOUser, fetchUserInfo, trackLogin }
    } = actions

    setSSOUser({
      accessToken: authTokens.AccessToken,
      refreshToken: authTokens.RefreshToken
    })
    datadogLogs.logger.info(
      '[Auth Debug - Auth Store] Resume Session Track Login'
    )

    await endpoints.trackProviderLogin.request()
    await fetchUserInfo()
    datadogLogs.logger.info('[Auth Debug - Auth Store] Fetched User Info')
    return trackLogin()
  }

  isAuthenticated = async () => {
    try {
      datadogLogs.logger.info(
        '[Auth Debug - Auth] Checking if User is Authenticated'
      )

      const session = await fetchAuthSession()

      if (session.tokens) {
        datadogLogs.logger.info('[Auth Debug - Auth] Session is valid')
        this.startAutoLogoutTimer(false)
        return session
      } else {
        datadogLogs.logger.warn('[Auth Debug - Auth] Session is not valid')
        throw new Error('Session is not valid')
      }
    } catch (error) {
      const ssoToken = getCookie('ssoAccessToken')
      if (ssoToken) {
        const {
          auth: { resumeSession }
        } = store.getActions()
        datadogLogs.logger.info('[Auth Debug - Auth] Resuming SSO Session')
        resumeSession()
      } else {
        // eslint-disable-next-line
        parent.postMessage({ type: 'BP_CLINICIAN_NOT_AUTHENTICATED' }, '*')
        throw new Error('User has not authenticated.')
      }
    }
  }

  //AUTO LOGOUT FUNCTIONALITY
  get lastAction() {
    return parseInt(store.get(STORE_KEY))
  }
  set lastAction(value) {
    store.set(STORE_KEY, value)
  }
  clearAutoLogoutTimer = () => {
    clearInterval(this.autoLogoutTimer)
  }

  startAutoLogoutTimer = shouldResetTime => {
    this.initListener()
    this.initInterval()
    if (shouldResetTime) {
      this.resetAutoLogoutTime()
    }
  }

  initListener = () => {
    document.body.addEventListener('click', () => this.resetAutoLogoutTime())
  }

  resetAutoLogoutTime = () => {
    this.lastAction = Date.now()
  }

  initInterval = () => {
    this.autoLogoutTimer = setInterval(() => {
      this.checkAutoLogout()
    }, CHECK_INTERVAL)
  }

  checkAutoLogout = () => {
    const now = Date.now()
    const timeleft = this.lastAction + MINUTES_UNITL_AUTO_LOGOUT * 60 * 1000
    const diff = timeleft - now
    const isTimeout = diff < 0

    if (isTimeout) {
      // this.logout();
    }
  }
}

export const auth = new AuthService()
